/** Rivers, and the impassable ridges that make zone borders into chokepoints.
 *
 * A river needs somewhere to run *to*, which is the whole reason it is computed
 * from a distance-to-water field rather than drawn as a wandering line: the
 * field is a height map in disguise, every river starts at a local maximum and
 * strictly descends, so it always reaches a water body and never dead-ends in a
 * meadow.
 */

import type { AdventureMapState, RiverKind, TerrainKind } from "@game/core";
import { TERRAINS, setRiver, setTerrain } from "@game/core";
import type { Grid } from "./grid.js";
import {
  allNeighbours, cheapestPath, flood, orthoNeighbours, terrainOf, walkable, xOf, yOf,
} from "./grid.js";
import { type Rng, int, shuffle } from "./rng.js";
import { NOISE_ONE, relief } from "./noise.js";

const UNREACHED = 0x3fffffff;

/** Cells stepped away from the nearest water cell. Water itself is 0. */
export function distanceToWater(m: AdventureMapState): Int32Array {
  const g: Grid = { width: m.width, height: m.height };
  const n = g.width * g.height;
  const dist = new Int32Array(n).fill(UNREACHED);
  const queue: number[] = [];
  for (let i = 0; i < n; i++) {
    if (terrainOf(m, i) === "water") { dist[i] = 0; queue.push(i); }
  }
  const buf: number[] = [];
  for (let head = 0; head < queue.length; head++) {
    const cur = queue[head]!;
    const d = dist[cur]! + 1;
    for (const nb of allNeighbours(g, cur, buf)) {
      if (dist[nb]! <= d) continue;
      dist[nb] = d;
      queue.push(nb);
    }
  }
  return dist;
}

const riverKindFor = (m: AdventureMapState, i: number): RiverKind => {
  switch (terrainOf(m, i)) {
    case "snow": return "ice";
    case "lava": return "lava";
    case "swamp": return "mud";
    default: return "water";
  }
};

/**
 * How far off the shortest line to the sea a channel is allowed to wander.
 *
 * The old field was `dist * 8 + noise(0..7)` with `noise` an independent draw
 * per cell. It looks like a meander control and is not one: because the wobble
 * is smaller than the scale, a neighbour one step nearer the sea always wins,
 * so the only freedom the walk ever had was which of several *equally* distant
 * neighbours to take, decided by a coin flip. A coin flip per step is a random
 * walk, and a random walk on a four-connected grid is a staircase of axis-
 * aligned runs - the same circuit-board reading the roads had, and measurably:
 * half the river cells sat in a straight run of six or more.
 *
 * A river bends because the ground bends, and the ground is the same over ten
 * cells running. So the choice is no longer a coin flip between the neighbours
 * that tie: it is the lowest one on a `relief` field, which agrees with the
 * choice ten steps ago because the field is smooth over `LONG_PERIOD` cells.
 * That turns the jitter into a meander.
 *
 * `RIVER_DRIFT` is what still guarantees arrival. Sideways steps are allowed in
 * runs of at most that many, and while `dist` is above zero a strictly nearer
 * neighbour always exists, so forcing one every `RIVER_DRIFT` steps bounds the
 * walk. `RIVER_MEANDER` only sets the resolution the field is compared at; any
 * value large enough to break ties between adjacent cells does the same job.
 */
const RIVER_MEANDER = 176;
const RIVER_DRIFT = 3;
const RIVER_SALT = 0x6d2b79f5;

/**
 * What a channel pays for the SHAPE of a step, on top of where the field leans.
 *
 * The meander field decides which WAY the river leans, and it does that well,
 * but it says nothing about the shape of the steps. The walk is eight-
 * connected now - the overlay atlas has a frame for a corner join, so a river
 * can run at 45 degrees as one ribbon - and two things keep it looking like
 * water rather than a wire. A step in the same heading as the last one is free
 * for the first `RIVER_RUN_FREE` and then costs `RIVER_RUN_TAX` a cell, which
 * is what turns a fifteen-cell lean into a run that drifts a heading either
 * side of it every few cells instead of fifteen identical steps. And a change
 * of heading is priced by how sharp it is: 45 degrees is what a river does,
 * 90 costs `RIVER_TURN_90`, and anything sharper is refused outright, because
 * a river does not double back on itself in one cell.
 *
 * The tax is a large fraction of `RIVER_MEANDER`, which is what makes it bite:
 * it has to be able to overturn the field's own preference after a few cells,
 * or the channel simply pays it and runs straight anyway.
 */
const RIVER_RUN_FREE = 2;
const RIVER_RUN_TAX = 34;
const RIVER_TURN_90 = 40;
/**
 * What a step onto a different terrain costs. A river takes its KIND from the
 * ground it runs over - ice on snow, lava on lava - and a source on snow whose
 * walk left the snow after two cells drew one cell of ice and forty of water:
 * on the Tower map every river sat on the dirt beside the snow, and the ice
 * overlay never showed. The tax keeps a channel on the ground it started on
 * for as long as the field allows, the way a snowfield's rivers run across the
 * snow in the classic, and it is large against `RIVER_MEANDER` because it has to
 * overturn the field's lean, not just break its ties.
 */
const RIVER_TERRAIN_CHANGE = 120;
/** Cells at least six steps from water a terrain needs to be owed a river. */
const RIVER_HINTERLAND = 40;
/** ...and per how many more it is owed another, up to three. */
const RIVER_HINTERLAND_WIDE = 80;
/**
 * Chebyshev radius around a source that later sources keep clear of. Wide,
 * because the farthest-from-water cells of a field all sit on the same
 * side of it: a snowfield with the sea to its west has its high ground
 * along its east edge, and three rivers from there all run east. Pushed
 * this far apart the third has to start in the middle of the snow and run
 * across it to the sea.
 */
const RIVER_SOURCE_SPACING = 9;

/**
 * Run `count` rivers from inland high ground down to the sea, then a lava flow
 * through every lava field big enough to hold one. Every stretch of at most
 * `RIVER_DRIFT` steps ends on one that is strictly nearer the water, so a
 * river is bounded, cannot loop, and arrives.
 */
export function carveRivers(m: AdventureMapState, r: Rng, count: number): number[][] {
  const g: Grid = { width: m.width, height: m.height };
  const dist = distanceToWater(m);
  const n = g.width * g.height;

  const sources: number[] = [];
  for (let i = 0; i < n; i++) {
    if (dist[i]! < 6 || dist[i]! === UNREACHED) continue;
    const t = terrainOf(m, i);
    // Lava fields get their own flows (`carveLavaFlows`); a sea-bound river
    // never starts on one, or crosses one, see `notLava`.
    if (t === "rock" || t === "lava") continue;
    sources.push(i);
  }
  if (sources.length === 0) return carveLavaFlows(m, []);
  shuffle(r, sources);
  sources.sort((a, b) => dist[b]! - dist[a]! || a - b);

  // Sources are dealt out across the terrains that have any, largest ground
  // first, so a map with a snowfield gets a river that STARTS on the snow
  // and not only ones that start on the biggest inland plain and happen to
  // cross it. Within a terrain the farthest-from-water cell still goes first.
  const byTerrain = new Map<TerrainKind, number[]>();
  for (const i of sources) {
    const t = terrainOf(m, i);
    const bucket = byTerrain.get(t);
    if (bucket === undefined) byTerrain.set(t, [i]); else bucket.push(i);
  }
  const buckets = [...byTerrain.values()].sort((a, b) => b.length - a.length || a[0]! - b[0]!);

  // ...and every terrain with a real hinterland is owed one, or two when the
  // hinterland is wide, whatever `count` says: a snowfield without an ice
  // river is a snowfield without its art, and the classic's snow maps carry several.
  const owed = buckets.reduce((sum, b) =>
    sum + (b.length >= RIVER_HINTERLAND ? Math.min(3, 1 + Math.trunc(b.length / RIVER_HINTERLAND_WIDE)) : 0), 0);
  const want = Math.max(count, owed);

  const carved: number[][] = [];
  const used = new Uint8Array(n);
  // Round-robin over the terrains, and within a round a terrain keeps
  // offering sources until one of them makes a river: a source whose walk
  // joins the first river three cells on is refused as too short, and a
  // deal of one source per terrain per round handed the snowfield's second
  // river to the rough beside it every time.
  const queues = buckets.map((b) => b.slice());
  while (carved.length < want && queues.some((q) => q.length > 0)) {
    for (const q of queues) {
      if (carved.length >= want) break;
      while (q.length > 0) {
        const src = q.shift()!;
        if (used[src] === 1) continue;
        const path = descend(m, g, dist, src, used, notLava(m));
        if (path === null) continue;
        for (const i of path) setRiver(m, xOf(g, i), yOf(g, i), 0, riverKindFor(m, i));
        carved.push(path);
        // The next source on the same ground comes from somewhere else: the
        // second-highest cell of a plateau is beside the highest, and a river
        // from there is a two-cell tributary of the first.
        const sx = xOf(g, src), sy = yOf(g, src);
        for (let dy = -RIVER_SOURCE_SPACING; dy <= RIVER_SOURCE_SPACING; dy++) {
          for (let dx = -RIVER_SOURCE_SPACING; dx <= RIVER_SOURCE_SPACING; dx++) {
            const x = sx + dx, y = sy + dy;
            if (x >= 0 && y >= 0 && x < g.width && y < g.height) used[y * g.width + x] = 1;
          }
        }
        break;
      }
    }
  }
  return carveLavaFlows(m, carved);
}

/**
 * Re-check every river against the FINAL terrain and put right the ones the
 * later passes broke.
 *
 * Rivers are carved before the ridges and the connectivity repair, because a
 * town site must not be chosen on one; but the repair turns water into sand
 * to bridge a strait, and a river whose mouth was on that water is left
 * ending two cells short of the sea on open sand, which is the one thing a
 * river never does. Each river's mouth is checked: it must touch water, or
 * be a lava flow at the edge of its field. One that does not is walked on
 * from its mouth down the current distance field; if even that cannot reach
 * water the whole channel is erased, since a channel that stops in the open
 * is worse than none. `blocked` is ground the walk may not enter - the town
 * sites, which were chosen dry. Returns the rivers as they now stand.
 */
/** Index of the lava kind in the river plane, as `setRiver` stores it. */
const RIVER_KIND_LAVA = 3;

/**
 * Water and lava are two CLASSES of river that never touch. A river takes its
 * kind from the ground it runs over, so a sea-bound river that crossed a lava
 * strip was drawn as water, then lava, then water - on screen an orange flow
 * pouring into a blue channel (seed 248, cell 18,30), which no the classic map does.
 * So a water-class walk refuses lava ground outright, a lava flow refuses any
 * cell beside a water-class channel and never joins one, and a tributary only
 * counts as arrived at a channel of its own class.
 */
const isLavaKind = (kind: number): boolean => kind === RIVER_KIND_LAVA;
const sameClass = (a: number, b: number): boolean => isLavaKind(a) === isLavaKind(b);
const notLava = (m: AdventureMapState) => (i: number): boolean => terrainOf(m, i) !== "lava";
/** No eight-neighbour of `i` carries a river of the other class than `kind`. */
function clearOfOtherClass(m: AdventureMapState, g: Grid, i: number, kind: number, buf: number[]): boolean {
  for (const nb of allNeighbours(g, i, buf)) {
    const k = m.river[nb]!;
    if (k !== 0 && !sameClass(k, kind)) return false;
  }
  return true;
}

export function repairRivers(
  m: AdventureMapState, rivers: readonly (readonly number[])[], blocked: ReadonlySet<number>,
): number[][] {
  const g: Grid = { width: m.width, height: m.height };
  const n = g.width * g.height;
  const buf: number[] = [];
  const lavaKind = RIVER_KIND_LAVA;
  const arrived = (mouth: number, own: ReadonlySet<number>): boolean => {
    const kind = m.river[mouth]!;
    // A lava flow ends where it cooled: at the edge of its field, or short
    // of a water channel it was refused. Its mouth may since have been
    // buried under a ridge (rock), which still reads as a flow out of the
    // mountain. Anything else is a flow the terrain passes broke.
    if (kind === lavaKind) {
      const t = terrainOf(m, mouth);
      return t === "lava" || t === "rock";
    }
    const nbs = allNeighbours(g, mouth, buf);
    if (nbs.some((nb) => terrainOf(m, nb) === "water")) return true;
    // a tributary's mouth is the channel it joined - of its own class
    return nbs.some((nb) => m.river[nb] !== 0 && !own.has(nb) && sameClass(m.river[nb]!, kind));
  };
  let dist: Int32Array | null = null;
  const used = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (m.river[i] !== 0) used[i] = 1;
  const out: number[][] = [];
  for (const river of rivers) {
    const mouth = river[river.length - 1];
    if (mouth === undefined) continue;
    if (arrived(mouth, new Set(river))) { out.push([...river]); continue; }
    if (dist === null) dist = distanceToWater(m);
    used[mouth] = 0;
    const kind = m.river[mouth]!;
    // ...but not through a town site, which was chosen dry, nor over lava
    // or beside a lava flow (a water-class mouth is the only kind that gets
    // here: see `arrived`)
    const more = descend(m, g, dist, mouth, used,
      (i) => !blocked.has(i) && terrainOf(m, i) !== "lava" && clearOfOtherClass(m, g, i, kind, buf),
      1, (nb) => sameClass(m.river[nb]!, kind));
    if (more !== null && more.length > 0) {
      for (const i of more) setRiver(m, xOf(g, i), yOf(g, i), 0, riverKindFor(m, i));
      out.push([...river.slice(0, -1), ...more]);
      continue;
    }
    for (const i of river) { setRiver(m, xOf(g, i), yOf(g, i), 0, "none"); used[i] = 0; }
  }
  return out;
}

/**
 * Lava flows: a river's walk, run over a lava field's own height map.
 *
 * A sea-bound river only crosses a lava field by accident, so the map's lava
 * almost never showed the lava overlay it has art for. But lava does not go to
 * the sea; it comes out of the middle of the field and runs to the edge and
 * stops, cooling. So the field the walk descends is the distance from the
 * field's own EDGE - the interior is the high ground - and the flow is kept
 * on lava: it ends on the last lava cell before the border, which is where a
 * flow ends. One per field of `LAVA_FIELD_MIN` cells or more, from the most
 * interior cell, which is the volcano whether or not one is drawn there.
 */
const LAVA_FIELD_MIN = 24;
const LAVA_FLOW_MIN_LEN = 4;

function carveLavaFlows(m: AdventureMapState, carved: number[][]): number[][] {
  const g: Grid = { width: m.width, height: m.height };
  const n = g.width * g.height;
  const lava = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (terrainOf(m, i) === "lava") lava[i] = 1;

  // distance to the nearest non-lava cell; non-lava is 0
  const edge = new Int32Array(n).fill(UNREACHED);
  const queue: number[] = [];
  for (let i = 0; i < n; i++) if (lava[i] === 0) { edge[i] = 0; queue.push(i); }
  const buf: number[] = [];
  for (let head = 0; head < queue.length; head++) {
    const cur = queue[head]!;
    const d = edge[cur]! + 1;
    for (const nb of allNeighbours(g, cur, buf)) {
      if (edge[nb]! <= d) continue;
      edge[nb] = d;
      queue.push(nb);
    }
  }

  // one flow per connected lava field, from its most interior cell
  const field = new Int32Array(n).fill(-1);
  const peaks: number[] = [];
  for (let i = 0; i < n; i++) {
    if (lava[i] === 0 || field[i] !== -1) continue;
    const id = peaks.length;
    const stack = [i];
    field[i] = id;
    let size = 0, peak = i;
    while (stack.length > 0) {
      const cur = stack.pop()!;
      size++;
      if (edge[cur]! > edge[peak]! || (edge[cur] === edge[peak] && cur < peak)) peak = cur;
      for (const nb of allNeighbours(g, cur, buf)) {
        if (lava[nb] === 0 || field[nb] !== -1) continue;
        field[nb] = id;
        stack.push(nb);
      }
    }
    peaks.push(size >= LAVA_FIELD_MIN && edge[peak]! >= 3 ? peak : -1);
  }

  const used = new Uint8Array(n);
  for (const i of carved.flat()) used[i] = 1;
  for (const peak of peaks) {
    if (peak < 0 || used[peak] === 1) continue;
    // `edge` of a non-lava cell is 0, and the walk stops before stepping onto
    // one, so the flow's mouth is the last lava cell of the field. A flow
    // never joins a channel and keeps a cell clear of any water-class river
    // running along the field's border; where that leaves it no step it
    // stops where it is, cooled, which is what a flow does.
    const path = descend(m, g, edge, peak, used,
      (i) => lava[i] === 1 && clearOfOtherClass(m, g, i, RIVER_KIND_LAVA, buf),
      LAVA_FLOW_MIN_LEN, () => false, true);
    if (path === null) continue;
    for (const i of path) setRiver(m, xOf(g, i), yOf(g, i), 0, "lava");
    carved.push(path);
  }
  return carved;
}

/**
 * Walk downhill on `dist` from `src` until it reaches 0 (or, when the walk is
 * confined by `allowed`, until no allowed step remains), and return the cells
 * of a path worth keeping, or null. Marks the path in `used`.
 */
function descend(
  m: AdventureMapState, g: Grid, dist: Int32Array, src: number, used: Uint8Array,
  allowed: (i: number) => boolean, minLen = 4,
  join: (nb: number) => boolean = (nb) => m.river[nb] !== 0,
  stopShort = false,
): number[] | null {
  const n = g.width * g.height;
  const onPath = new Uint8Array(n);
  const buf: number[] = [];
  const meander = (i: number): number =>
    Math.trunc((relief(xOf(g, i), yOf(g, i), RIVER_SALT) * RIVER_MEANDER) / NOISE_ONE);
  const wet = (i: number): boolean => m.river[i] !== 0 || onPath[i] === 1;

  /**
   * A channel is one cell wide, everywhere, including at the mouth.
   *
   * Four river cells in a square would be a delta in the classic, which has delta art
   * (`clrdelt` and friends) keyed off a wider pattern. Our overlay atlas has
   * no delta frame, so a square of four renders as four four-way junctions
   * with their outer corners bare - a ring of water around a hole of grass,
   * the same artefact as a closed road loop. Until there is delta art there is
   * no case where the square is wanted.
   */
  const wouldPool = (i: number): boolean => {
    const x = xOf(g, i), y = yOf(g, i);
    for (const [dx, dy] of [[-1, -1], [0, -1], [-1, 0], [0, 0]] as const) {
      const a = x + dx, b = y + dy;
      if (a < 0 || b < 0 || a + 1 >= g.width || b + 1 >= g.height) continue;
      const q = [b * g.width + a, b * g.width + a + 1, (b + 1) * g.width + a, (b + 1) * g.width + a + 1];
      if (q.every((c) => c === i || wet(c))) return true;
    }
    return false;
  };

  const path: number[] = [];
  let cur = src;
  let guard = 0;
  let drift = 0;
  let lastDir = -1;
  let run = 0;
  // A walk that reaches an EXISTING channel has arrived: it is a tributary,
  // and the channel it joined already reaches the water. Without this a
  // second river crossed the first, or ran beside it and rejoined it, and the
  // map had river loops enclosing islands of grass - a thing a river cannot
  // do, and the first thing a screenshot of two rivers showed.
  let joined = false;
  // ...and one that ran out of allowed steps has, when `stopShort` says a
  // walk may end where it is (a lava flow cools), also arrived.
  let stopped = false;
  while (dist[cur]! > 0 && guard++ < n) {
    path.push(cur);
    onPath[cur] = 1;
    const here = dist[cur]!;
    const cx = xOf(g, cur), cy = yOf(g, cur);
    // Heading as an index into the eight-neighbour table, clockwise from N.
    const dirOf = (nb: number): number => {
      const dx = xOf(g, nb) - cx, dy = yOf(g, nb) - cy;
      return dy < 0 ? (dx < 0 ? 7 : dx > 0 ? 1 : 0)
        : dy > 0 ? (dx < 0 ? 5 : dx > 0 ? 3 : 4) : dx > 0 ? 2 : 6;
    };
    const turnOf = (nb: number): number => {
      if (lastDir < 0) return 0;
      const t = Math.abs(dirOf(nb) - lastDir);
      return t > 4 ? 8 - t : t;
    };
    // A corner step is a river only when it will be DRAWN as one: neither
    // flank may already be wet, or the join is an elbow through the flank
    // and the channel has touched itself.
    const corner = (nb: number): boolean => {
      const nx = xOf(g, nb), ny = yOf(g, nb);
      if (nx === cx || ny === cy) return true;
      return !wet(ny * g.width + cx) && !wet(cy * g.width + nx);
    };
    // An existing channel is in `open` only when this walk may JOIN it; one
    // it may not join is not a cell it may step onto either.
    const open = allNeighbours(g, cur, buf)
      .filter((nb) => onPath[nb] !== 1 && dist[nb]! <= here && !wouldPool(nb)
        && corner(nb) && turnOf(nb) <= 2 && (dist[nb] === 0 || allowed(nb))
        && (m.river[nb] === 0 || join(nb)));
    if (open.some((nb) => m.river[nb] !== 0)) { joined = true; break; }
    const closer = open.filter((nb) => dist[nb]! < here);
    // Sideways is allowed only in runs of `RIVER_DRIFT`; after that the
    // channel has to close on the sea again, which is what terminates it.
    const pool = drift < RIVER_DRIFT && open.length > 0 ? open : closer;
    if (pool.length === 0) { stopped = true; break; }
    // Lowest ground wins, ties by cell index: the choice is a function of the
    // map and not of the order the neighbours came back in.
    const ground = terrainOf(m, cur);
    const score = (nb: number): number =>
      meander(nb)
      + (dirOf(nb) === lastDir ? RIVER_RUN_TAX * Math.max(0, run - RIVER_RUN_FREE) : 0)
      + (turnOf(nb) === 2 ? RIVER_TURN_90 : 0)
      + (dist[nb] !== 0 && terrainOf(m, nb) !== ground ? RIVER_TERRAIN_CHANGE : 0);
    let next = pool[0]!;
    let best = score(next);
    for (const nb of pool) {
      const v = score(nb);
      if (v < best || (v === best && nb < next)) { next = nb; best = v; }
    }
    // A confined walk stops on the last allowed cell rather than stepping
    // off the field: the mouth of a lava flow is the edge of the lava.
    if (dist[next] === 0 && !allowed(next)) { stopped = true; break; }
    const took = dirOf(next);
    run = took === lastDir ? run + 1 : 1;
    lastDir = took;
    drift = dist[next]! < here ? 0 : drift + 1;
    cur = next;
  }
  // Only keep a walk that actually arrived - at the water, or at the edge of
  // the field it was confined to; a stub reads as a mistake.
  const arrived = joined || dist[cur] === 0 || (stopShort && stopped)
    || (path.length > 0 && allNeighbours(g, cur, buf).some((nb) => dist[nb] === 0));
  if (!arrived || path.length < minLen) return null;
  // ...and only one that stayed a channel. The step filter refuses to *enter*
  // a cell that squares off, but a source can be dropped beside an existing
  // channel with the square already three-quarters made, so the finished path
  // is checked as a whole before it is committed.
  if (path.some((i) => wouldPool(i))) return null;
  for (const i of path) used[i] = 1;
  return path;
}

// -------------------------------------------------------------------- ridges

export interface RidgeResult {
  /** Cells left open through a wall; each is a natural place for a guard. */
  readonly gates: readonly number[];
}

/**
 * Wall the seams between zones with impassable rock, leaving one or two gaps
 * per seam. That is what turns a zone into a *region* with chokepoints instead
 * of an open field with a colour change, and it is what a guard is for.
 *
 * Connectivity is repaired afterwards rather than reasoned about up front:
 * walling is cheap, and `repairConnectivity` reopens the cheapest line of rock
 * between two components until the whole land mass is one piece again.
 */
export function raiseRidges(
  m: AdventureMapState, r: Rng, zoneOf: Int32Array,
  adjacency: readonly (readonly number[])[], protectedCells: ReadonlySet<number>,
  wallNeutralBp: number,
): RidgeResult {
  const g: Grid = { width: m.width, height: m.height };
  const gates: number[] = [];
  const buf: number[] = [];

  const seen = new Set<string>();
  for (let a = 0; a < adjacency.length; a++) {
    for (const b of adjacency[a] ?? []) {
      if (b <= a) continue;
      const key = `${a}:${b}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const border: number[] = [];
      for (const i of zoneCells(zoneOf, a)) {
        for (const nb of orthoNeighbours(g, i, buf)) {
          if (zoneOf[nb] === b) { border.push(i); break; }
        }
      }
      if (border.length < 6) continue;
      const anyWater = border.some((i) => terrainOf(m, i) === "water");
      if (anyWater) continue;
      if (!chance10k(r, wallNeutralBp)) continue;

      border.sort((x, y) => x - y);
      const gateCount = 1 + int(r, 2);
      const chosen: number[] = [];
      for (let k = 0; k < gateCount; k++) {
        const at = Math.trunc(((k * 2 + 1) * border.length) / (gateCount * 2));
        chosen.push(border[Math.min(border.length - 1, at)]!);
      }
      for (const c of chosen) gates.push(c);

      for (const i of border) {
        if (protectedCells.has(i)) continue;
        if (chosen.some((c) => chebyshevBetween(g, c, i) <= 1)) continue;
        setTerrain(m, xOf(g, i), yOf(g, i), 0, "rock");
      }
    }
  }
  return { gates };
}

const chance10k = (r: Rng, bp: number): boolean => int(r, 10000) < bp;

const chebyshevBetween = (g: Grid, a: number, b: number): number =>
  Math.max(Math.abs(xOf(g, a) - xOf(g, b)), Math.abs(yOf(g, a) - yOf(g, b)));

function* zoneCells(zoneOf: Int32Array, z: number): Generator<number> {
  for (let i = 0; i < zoneOf.length; i++) if (zoneOf[i] === z) yield i;
}

/**
 * Open ground until every cell in `mustConnect` is in one walkable component.
 *
 * Two kinds of obstruction get opened, and they are opened differently. Rock is
 * a ridge the generator raised, so it is put back to whatever it covered. Water
 * is not something to undo - a strait that has to be crossed becomes a sandbar,
 * three cells wide so it reads as an isthmus and not as a one-pixel scratch.
 *
 * The line is the cheapest path that treats both as expensive but passable, so
 * the crossing is the shortest one that exists rather than the first one found.
 * `blocked` cells are not crossed at any price.
 */
export function repairConnectivity(
  m: AdventureMapState, mustConnect: readonly number[], fallback: Record<number, number>,
  bridgeWater = true, blocked: ReadonlySet<number> = new Set(),
): number {
  if (mustConnect.length === 0) return 0;
  const g: Grid = { width: m.width, height: m.height };
  const buf: number[] = [];
  let opened = 0;
  for (let round = 0; round < 32; round++) {
    const root = mustConnect[0]!;
    if (!walkable(m, root)) return opened;
    const seen = blocked.size === 0 ? flood(m, root) : floodAvoiding(m, root, blocked);
    const stranded = mustConnect.find((i) => seen[i] !== 1);
    if (stranded === undefined) return opened;

    const cost = (i: number): number => {
      // `blocked` is ground that will be built on before anyone walks here -
      // the town footprints - so a line through it connects nothing
      if (blocked.has(i)) return -1;
      if (walkable(m, i)) return 1;
      if (terrainOf(m, i) === "water") return bridgeWater ? 120 : -1;
      return 60;
    };
    const path = cheapestPath(g, stranded, root, cost);
    if (path === null) return opened;
    for (const i of path) {
      if (walkable(m, i)) continue;
      if (terrainOf(m, i) === "water") {
        setTerrain(m, xOf(g, i), yOf(g, i), 0, "sand");
        for (const nb of orthoNeighbours(g, i, buf)) {
          if (terrainOf(m, nb) === "water") setTerrain(m, xOf(g, nb), yOf(g, nb), 0, "sand");
        }
      } else {
        const t = fallback[i];
        setTerrain(m, xOf(g, i), yOf(g, i), 0, t === undefined ? "dirt" : terrainName(t));
      }
      opened++;
    }
  }
  return opened;
}

const terrainName = (i: number): TerrainKind => TERRAINS[i] ?? "dirt";

/**
 * `flood`, but treating `blocked` cells as walls. A doorstep on a strip of
 * land that a town's own footprint will wall off is reachable today and
 * stranded the moment the town is stamped, so the repair has to see the
 * footprint or it repairs nothing (seed 4242, eight players at 48).
 */
function floodAvoiding(m: AdventureMapState, origin: number, blocked: ReadonlySet<number>): Uint8Array {
  const g: Grid = { width: m.width, height: m.height };
  const seen = new Uint8Array(g.width * g.height);
  if (!walkable(m, origin) || blocked.has(origin)) return seen;
  const stack = [origin];
  const buf: number[] = [];
  seen[origin] = 1;
  while (stack.length > 0) {
    const cur = stack.pop()!;
    for (const nb of allNeighbours(g, cur, buf)) {
      if (seen[nb] === 1 || blocked.has(nb) || !walkable(m, nb)) continue;
      seen[nb] = 1;
      stack.push(nb);
    }
  }
  return seen;
}
